import { useEffect, useRef } from "react";

/**
 * Run `callback` on an interval, but PAUSE while the browser tab is hidden
 * (document.hidden). When the tab becomes visible again we fire immediately
 * and resume. This stops background tabs from hammering the backend — the
 * single biggest, safest win against the app's polling load.
 *
 * @param {Function} callback   async/void function to run each tick
 * @param {number}   intervalMs polling interval in ms
 * @param {object}   opts       { immediate = true, enabled = true }
 */
export function useVisibilityPolling(callback, intervalMs, opts = {}) {
  const { immediate = true, enabled = true } = opts;
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    if (!enabled || !intervalMs) return;
    let timer = null;

    const tick = () => {
      if (!document.hidden) cbRef.current && cbRef.current();
    };
    const start = () => {
      if (!timer) timer = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        cbRef.current && cbRef.current(); // instant refresh on return
        start();
      }
    };

    if (immediate && !document.hidden && cbRef.current) cbRef.current();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs, enabled, immediate]);
}
